import { useQueries, useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import type { ChosenTool } from './app-lending-flow';
import { apiFetch } from './api-client';
import { queryKeys } from './query-client';

// Client data layer for mini-app sharing: a Space's apps, and who can open one of them.
//
// orion-web's CLIENT bundle must not import common-lib (same rule as lib/workspace-files.ts), so the two
// item shapes below are client-local contracts matching what pages/api/workspace-apps.ts and
// pages/api/app-shares.ts serve. Every call goes through `apiFetch`/`API_BASE`.
//
// The states are the discriminated union the rest of the app uses. `error` is a REAL state here, not a
// styling detail: an app is private exactly when its share list is empty AND the read succeeded, so
// collapsing a failed read into `{ shares: [] }` would render "Private" over an unknown.

/** One mini-app in a Space. Mirrors `WorkspaceAppSummary` in pages/api/workspace-apps.ts. */
export interface WorkspaceApp {
  id: string;
  /** Which Space owns it. Absent on the per-space read (the caller already knows); stamped by
   *  `useAllWorkspaceApps`, because a cross-space list cannot be filtered by a Space it never recorded. */
  workspaceId?: string;
  name: string;
  url?: string;
  thumbnailUrl?: string;
  description?: string;
  updatedDate?: string;
  /** When the app was registered. Shown on the card, and the tiebreak once recency runs out. */
  createdDate?: string;
  /** ISO string; when THIS viewer last opened the app. Absent means "not opened recently". */
  lastOpenedAt?: string;
  /** The owner's name when the app is somebody else's and the name resolved. See the API route. */
  ownerName?: string;
}

/** One grantee. Mirrors `AppShareSummary` in pages/api/app-shares.ts. */
export interface AppShareItem {
  granteeRecipientId: string;
  /** Both of these are optional on the wire — a row with neither is still a real grant. */
  granteeEmail?: string;
  granteeName?: string;
  /** True when this share created the person: nobody answered to that address until now. */
  granteeIsNew?: boolean;
  createdDate?: string;
}

export type WorkspaceAppsState = { status: 'loading' } | { status: 'ready'; apps: WorkspaceApp[] } | { status: 'error' };

// `failedSpaces` rides along with the apps rather than collapsing into `error`: some spaces answering
// is a different fact from none answering, and the screen has to be able to say so.
export type AllWorkspaceAppsState = { status: 'loading' } | { status: 'ready'; apps: WorkspaceApp[]; failedSpaces: number } | { status: 'error' };

export type AppSharesState = { status: 'loading' } | { status: 'ready'; shares: AppShareItem[] } | { status: 'error' };

export async function fetchWorkspaceApps(workspaceId: string): Promise<WorkspaceApp[]> {
  const res = await apiFetch(`${API_BASE}/api/workspace-apps?workspaceId=${encodeURIComponent(workspaceId)}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  const data = (await res.json()) as { apps?: WorkspaceApp[] };
  // Stamped HERE, not at one call site, because `workspaceId` is now load-bearing twice over: the
  // cross-space screen filters by it, and every screen reads its presence as "this app is mine"
  // (a shared app lives in no Space of yours and so carries none). Stamping it in only one of the
  // two hooks made the Delete control appear on the cross-space list and vanish inside a Space,
  // for the same app.
  return (data.apps ?? []).map((app) => ({ ...app, workspaceId }));
}

export async function fetchAppShares(appId: string): Promise<AppShareItem[]> {
  const res = await apiFetch(`${API_BASE}/api/app-shares?appId=${encodeURIComponent(appId)}`, { credentials: 'include' });
  if (!res.ok) {
    // Throwing is the point: the hook turns this into `{ status: 'error' }`, which the dialog renders as
    // "couldn't load who has access" + retry. Returning [] here would say "Private" instead.
    throw new Error(`HTTP ${res.status}`);
  }
  const data = (await res.json()) as { shares?: AppShareItem[] };
  return data.shares ?? [];
}

export async function shareApp(appId: string, email: string, lendTools: ChosenTool[] = []): Promise<AppShareItem | null> {
  const res = await apiFetch(`${API_BASE}/api/app-shares`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    // An empty list is the ordinary share and the default: the grantee's copy runs against THEIR
    // Symphony and reaches nobody else. It is sent only when non-empty so a plain share cannot be
    // mistaken for a lending one anywhere down the chain.
    body: JSON.stringify({ appId, email, ...(lendTools.length ? { lendTools } : {}) }),
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  const data = (await res.json()) as { share?: AppShareItem | null };
  return data.share ?? null;
}

export async function revokeAppShare(appId: string, granteeRecipientId: string): Promise<void> {
  // The two ids ride the query string as well as the body: a DELETE body is not carried by every client
  // or proxy, and losing the grantee id silently would be a revoke aimed at nobody.
  const query = `appId=${encodeURIComponent(appId)}&granteeRecipientId=${encodeURIComponent(granteeRecipientId)}`;
  const res = await apiFetch(`${API_BASE}/api/app-shares?${query}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ appId, granteeRecipientId }),
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
}

export function useWorkspaceApps(workspaceId: string | undefined): WorkspaceAppsState {
  const query = useQuery({
    queryKey: queryKeys.workspaceApps(workspaceId),
    queryFn: () => fetchWorkspaceApps(workspaceId as string),
    enabled: !!workspaceId,
  });

  if (query.data) return { status: 'ready', apps: query.data };
  if (query.status === 'error') return { status: 'error' };
  return { status: 'loading' };
}

/**
 * Every app the user has, across every space — what the cross-space Files screen shows.
 *
 * One query per workspace rather than a new aggregate endpoint: `getWorkspaceArtifacts` is already the
 * per-space read, `useQueries` shares its cache with the per-space screen (so opening a space after
 * this list is free), and there is no server round trip to design, deploy and permission for a view
 * that is a concatenation.
 *
 * A workspace that FAILS is reported, not skipped. Dropping it would silently show a shorter list —
 * the user would see "3 apps" when they have four and have no way to tell an empty space from an
 * unreadable one. Partial data still renders, with the failure named alongside it.
 */
export function useAllWorkspaceApps(workspaceIds: string[]): AllWorkspaceAppsState {
  const queries = useQueries({
    queries: workspaceIds.map((id) => ({
      queryKey: queryKeys.workspaceApps(id),
      queryFn: () => fetchWorkspaceApps(id),
    })),
  });

  if (queries.some((q) => q.isLoading)) return { status: 'loading' };

  // `fetchWorkspaceApps` already stamps the Space onto each app; this only flattens.
  const apps = queries.flatMap((q) => q.data ?? []);
  const failed = queries.filter((q) => q.status === 'error').length;
  // Every space failed: there is nothing to show and nothing to claim, so this is an error, not "none".
  if (failed > 0 && apps.length === 0) return { status: 'error' };
  return { status: 'ready', apps, failedSpaces: failed };
}

export function useAppShares(appId: string | undefined): AppSharesState {
  const query = useQuery({
    queryKey: queryKeys.appShares(appId),
    queryFn: () => fetchAppShares(appId as string),
    enabled: !!appId,
    // A share list is a permission list — a stale one is a wrong answer about who can see something, so
    // it is re-read whenever the dialog mounts rather than served from cache.
    staleTime: 0,
    // Nothing here is worth a "Private" flash: a failed read stays a failed read (see fetchAppShares).
    retry: 1,
  });

  if (query.data) return { status: 'ready', shares: query.data };
  if (query.status === 'error') return { status: 'error' };
  return { status: 'loading' };
}

/**
 * The apps OTHER people shared with this user.
 *
 * <p>Nothing identifying is sent: the caller is resolved from their own signed identity server-side.
 *
 * <p>A failed read THROWS rather than resolving to `[]`, for the same reason `fetchAppShares` does —
 * "nobody shared anything with you" and "we could not find out" are different answers, and the screen
 * has to be able to tell them apart.
 */
export async function fetchSharedApps(): Promise<{ apps: WorkspaceApp[]; pending: WorkspaceApp[] }> {
  const res = await apiFetch(`${API_BASE}/api/shared-apps`, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`shared-apps ${res.status}`);
  }
  const body = (await res.json()) as { apps?: WorkspaceApp[]; pending?: WorkspaceApp[] };
  // `pending` absent reads as no offers — which is the right answer from a server that cannot record
  // one, and keeps this working through a rollout where the field does not exist yet.
  return { apps: body.apps ?? [], pending: body.pending ?? [] };
}

/**
 * Answer a share somebody offered: take it, or turn it down.
 *
 * <p>Sends the app id and the answer, and nothing else. The share is (this app, whoever is asking),
 * resolved from the caller's own identity server-side, so there is deliberately no grantee field for a
 * caller to point at somebody else's offer.
 */
/**
 * Stamp "I just opened this app", so the list can order by it.
 *
 * <p><b>Never lets a failure reach the caller.</b> Opening the app is the thing the user did; recording
 * it is our bookkeeping about that. So this resolves either way and the card fires it without awaiting
 * — the app's tab opens at the same instant regardless of what the server says. The server takes the
 * same view: the rpc answers an empty message and reports no failure of its own.
 *
 * <p>No viewer field: whose recency this is comes from the caller's own identity server-side.
 */
export async function recordAppOpen(appId: string): Promise<void> {
  try {
    await apiFetch(`${API_BASE}/api/app-opened`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ appId }),
    });
  } catch {
    // Deliberately swallowed — see above. The ordering simply stays as it was.
  }
}

export async function answerAppShare(appId: string, answer: 'accept' | 'decline'): Promise<void> {
  const res = await apiFetch(`${API_BASE}/api/app-share-answer`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ appId, answer }),
  });
  if (!res.ok) {
    throw new Error(`app-share-answer ${res.status}`);
  }
}

/**
 * Apps shared with this user, as its own state.
 *
 * <p>Deliberately NOT folded into {@link useAllWorkspaceApps}: an app somebody shared with you is not in
 * any Space of yours, so it has no `workspaceId`, cannot carry the Space filter, and must not be counted
 * in the "N apps across M spaces" line. Merging the two lists would make all three of those subtly wrong.
 *
 * <p>This surface reached iOS and Android before it reached web: both native stores fetched the shared
 * list and rendered nothing, and web had no consumer at all — so an app shared with you was unreachable
 * everywhere. This hook is the web half.
 */
export function useSharedApps(): WorkspaceAppsState {
  const query = useQuery({
    queryKey: queryKeys.sharedApps(),
    queryFn: fetchSharedApps,
  });
  if (query.isLoading) return { status: 'loading' };
  if (query.status === 'error') return { status: 'error' };
  return { status: 'ready', apps: query.data?.apps ?? [] };
}

/**
 * Shares OFFERED to this user and not yet answered.
 *
 * <p>Its own hook over the same read, rather than a field on {@link useSharedApps}: an offer is not an
 * app you have, and every surface that lists "your apps" must not be able to pick these up by
 * accident. They share one query key, so answering an offer refreshes both lists at once.
 */
export function usePendingAppShares(): WorkspaceAppsState {
  const query = useQuery({
    queryKey: queryKeys.sharedApps(),
    queryFn: fetchSharedApps,
  });
  if (query.isLoading) return { status: 'loading' };
  if (query.status === 'error') return { status: 'error' };
  return { status: 'ready', apps: query.data?.pending ?? [] };
}
